import { useMemo } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { PageContainer } from '@/components/layout/PageContainer'
import { Card, CardContent } from '@/components/ui/card'
import { useAuthStore } from '@/lib/store/authStore'
import { useStoreList } from '@/hooks/useStoreList'
import { StoreListCard } from '@/components/storefront/StoreListCard'
import { StorefrontSettingsGuard } from './StorefrontSettingsGuard'
import {
  MessageCircle,
  Users,
  DollarSign,
  Eye,
  ShoppingCart,
  Truck,
  Globe,
  Percent,
  ChevronRight,
  Palette,
  Shield,
  BarChart3,
  Gauge,
  Languages,
} from 'lucide-react'

const settingsCategories = [
  {
    icon: Palette,
    title: 'Branding',
    description: 'Link preview image and description',
    to: '/storefront/settings/branding',
    type: 'navigate' as const,
  },
  {
    icon: MessageCircle,
    title: 'LINE Integration',
    description: 'Social login and messaging API',
    to: '/storefront/settings/line',
    type: 'navigate' as const,
  },
  {
    icon: Users,
    title: 'Customer Accounts',
    description: 'Login, OTP, and profile fields',
    to: '/storefront/settings/accounts',
    type: 'navigate' as const,
  },
  {
    icon: DollarSign,
    title: 'Pricing',
    description: 'Price lists and stock sources',
    to: '/storefront/settings/pricing',
    type: 'navigate' as const,
  },
  {
    icon: Eye,
    title: 'Product Display',
    description: 'Viewer counts, filters, and stock display',
    to: '/storefront/settings/display',
    type: 'navigate' as const,
  },
  {
    icon: ShoppingCart,
    title: 'Orders & Payments',
    description: 'Slip verification and invoicing',
    to: '/storefront/settings/orders',
    type: 'navigate' as const,
  },
  {
    icon: Truck,
    title: 'Shipping & Reviews',
    description: 'Shipping config and customer reviews',
    to: '/storefront/settings/shipping',
    type: 'navigate' as const,
  },
  {
    icon: Globe,
    title: 'Custom Domain',
    description: 'Buy or connect your own domain',
    to: '/storefront/domains',
    type: 'link' as const,
  },
  {
    icon: Shield,
    title: 'PDPA / Privacy',
    description: 'Cookie consent banner for PDPA compliance',
    to: '/storefront/settings/pdpa',
    type: 'navigate' as const,
  },
  {
    icon: BarChart3,
    title: 'Ads Tracking',
    description: 'Meta Pixel, Google Ads, GA4 tracking',
    to: '/storefront/settings/ads-tracking',
    type: 'navigate' as const,
  },
  {
    icon: Languages,
    title: 'Languages',
    description: 'Multi-language storefront content',
    to: '/storefront/settings/languages',
    type: 'navigate' as const,
  },
  {
    icon: Gauge,
    title: 'Site Speed',
    description: 'ความเร็วเว็บไซต์ + Core Web Vitals (Google Lighthouse)',
    to: '/storefront/settings/site-speed',
    type: 'navigate' as const,
  },
  {
    icon: Percent,
    title: 'Promotion Tiers',
    description: 'Quantity-based tiered promotions',
    to: '/storefront/promotions',
    type: 'link' as const,
  },
]

export function StorefrontSettingsPage() {
  const navigate = useNavigate()
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl)
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey)
  const { data: stores = [] } = useStoreList()

  const scopedStorefrontUrl = useMemo(() => {
    if (!activeStorefrontKey) return storefrontUrl
    const store = stores.find((s) => s.storefront_key === activeStorefrontKey)
    return store?.domain ? `https://${store.domain}` : storefrontUrl
  }, [activeStorefrontKey, stores, storefrontUrl])

  return (
    <StorefrontSettingsGuard>
      <PageContainer>
        <div className="p-6 space-y-6">
          <div>
            <h2 className="text-2xl font-semibold mb-2">
              Storefront Settings
            </h2>
            <p className="text-muted-foreground">
              Manage your online store configuration
            </p>
          </div>

          {/* Stores section */}
          {stores.length > 0 && <StoreListCard />}

          {/* Default URL */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
              Default URL
            </h3>
            <div className="pl-4">
              <p className="text-sm text-muted-foreground">
                Your storefront is always accessible at:{' '}
                <a
                  href={scopedStorefrontUrl ?? ''}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline"
                >
                  {scopedStorefrontUrl}
                </a>
              </p>
            </div>
          </div>

          {/* Category cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {settingsCategories.map((cat) => {
              const cardContent = (
                <Card className="rounded-2xl border-0 hover:bg-muted/50 transition-colors cursor-pointer">
                  <CardContent className="p-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                        <cat.icon className="w-4.5 h-4.5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{cat.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {cat.description}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </CardContent>
                </Card>
              )

              if (cat.type === 'link') {
                return (
                  <Link key={cat.to} to={cat.to}>
                    {cardContent}
                  </Link>
                )
              }

              return (
                <div
                  key={cat.to}
                  onClick={() => navigate(cat.to)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(cat.to)
                    }
                  }}
                >
                  {cardContent}
                </div>
              )
            })}
          </div>
        </div>
      </PageContainer>
    </StorefrontSettingsGuard>
  )
}
